// Settings › Campaigns' ad spend (BL-008): what each campaign cost, entered by hand for a day or a
// month (an imported entry is corrected where it came from), and the ad platforms' campaigns linked
// to the company's, so their spend counts on the right campaign. The campaigns report reads it for
// cost per lead, per booked job and the return on ad spend.

import { useEffect, useState } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { money } from '../review/review-model.ts';
import { SaveButton } from '../shared/SaveButton.tsx';
import { useDesign } from '../blueprint/design.ts';
import { allSpend, type SpendPage } from '../blueprint/ads-model.ts';
import { ConversionsPart } from './AdConversions.tsx';
import { CHANNEL_WORDS, CHANNELS, type Campaign, type Channel } from './campaigns-model.ts';
import {
  centsTyped,
  editable,
  spendMeans,
  unlinkedMeans,
  type SpendEntry,
  type Unlinked,
} from './spend-model.ts';

// the server's words, or the form's own (an amount that isn't one)
const say = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Could not save. Try again.';

interface Link {
  id: string;
  platform: Unlinked['platform'];
  platformCampaignId: string | null;
  platformCampaignName: string | null;
  campaign: { id: string; name: string };
}
type Draft = { campaignId: string; channel: Channel; from: string; to: string; amount: string; note: string };

const isoDay = (d: Date) => d.toISOString().slice(0, 10);

export function AdSpendCards({ canEdit, campaigns }: { canEdit: boolean; campaigns: Campaign[] }) {
  // Blueprint: the linked campaigns are a part of the ad spend's section, under a hairline, not a section
  // of their own (at most three sections in view)
  const bp = useDesign() === 'blueprint';
  const Linked = bp ? 'div' : 'section';
  const [entries, setEntries] = useState<SpendEntry[] | null>(null);
  const [links, setLinks] = useState<{ links: Link[]; unlinked: Unlinked[] } | null>(null);
  const [again, setAgain] = useState(0);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [linking, setLinking] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const today = isoDay(new Date());
  useEffect(() => {
    const to = isoDay(new Date());
    const from = isoDay(new Date(Date.now() - 92 * 86_400_000));
    void Promise.all([
      // every page of the 92 days (list caps M11), not the first
      allSpend((input) => client.call<SpendPage<SpendEntry>>('adSpend.list', input), { from, to }),
      client.call<{ links: Link[]; unlinked: Unlinked[] }>('adCampaignLinks.list', {}),
    ])
      .then(([s, l]) => {
        setEntries(s);
        setLinks(l);
      })
      .catch((e: unknown) => setError(say(e)));
  }, [again]);
  const live = campaigns.filter((c) => c.active);
  const write = async (run: () => Promise<string>) => {
    setBusy(true);
    setError(null);
    try {
      setSaid(await run());
      setDraft(null);
      setAgain((n) => n + 1);
    } catch (e) {
      setError(say(e));
    } finally {
      setBusy(false);
    }
  };
  const record = (d: Draft) =>
    write(async () => {
      const cents = centsTyped(d.amount);
      if (cents === null) throw new Error('Type the amount in dollars, like 1,250.00.');
      await client.call('adSpend.record', {
        campaignId: d.campaignId || null,
        ...(d.campaignId ? {} : { channel: d.channel }),
        from: d.from,
        to: d.to,
        amountCents: cents,
        note: d.note.trim() || null,
      });
      return `${money(cents)} recorded.`;
    });
  const unlinkedKey = (u: Unlinked) =>
    `${u.platform}|${u.platformCampaignId ?? ''}|${u.platformCampaignName ?? ''}`;

  return (
    <>
      {said && (
        <p className="notice ok" role="status">
          {said}
        </p>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <section className="stone card" aria-label="Ad spend">
        <div className="row between wrap">
          <h2 className="h3">Ad spend</h2>
          {canEdit && !draft && (
            <button
              type="button"
              className="btn"
              onClick={() =>
                setDraft({
                  campaignId: live[0]?.id ?? '',
                  channel: 'google_ads',
                  from: today,
                  to: today,
                  amount: '',
                  note: '',
                })
              }
            >
              Add spend
            </button>
          )}
        </div>
        <p className="muted">
          What each campaign cost, for a day or a whole month as its invoice says. The campaigns report
          spreads it over the days, for cost per lead and the return on ad spend. What’s imported is corrected
          where it came from.
        </p>
        {draft && (
          <form
            className="fgrid campaign-form"
            aria-label="New ad spend"
            onSubmit={(e) => {
              e.preventDefault();
              if (!busy) void record(draft);
            }}
          >
            <label className="wide">
              <span className="flabel">Spent on</span>
              <select
                className="field"
                value={draft.campaignId}
                onChange={(e) => setDraft({ ...draft, campaignId: e.target.value })}
              >
                {live.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
                <option value="">No campaign: just a channel</option>
              </select>
            </label>
            {!draft.campaignId && (
              <label>
                <span className="flabel">Channel</span>
                <select
                  className="field"
                  value={draft.channel}
                  onChange={(e) => setDraft({ ...draft, channel: e.target.value as Channel })}
                >
                  {CHANNELS.map((ch) => (
                    <option key={ch} value={ch}>
                      {CHANNEL_WORDS[ch]}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              <span className="flabel">From</span>
              <input
                className="field"
                type="date"
                value={draft.from}
                onChange={(e) => setDraft({ ...draft, from: e.target.value })}
              />
            </label>
            <label>
              <span className="flabel">To</span>
              <input
                className="field"
                type="date"
                value={draft.to}
                onChange={(e) => setDraft({ ...draft, to: e.target.value })}
              />
            </label>
            <label>
              <span className="flabel">Amount</span>
              <input
                className="field"
                inputMode="decimal"
                placeholder="1,250.00"
                value={draft.amount}
                onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
              />
            </label>
            <label className="wide">
              <span className="flabel">Note</span>
              <input
                className="field"
                maxLength={500}
                placeholder="October’s invoice"
                value={draft.note}
                onChange={(e) => setDraft({ ...draft, note: e.target.value })}
              />
            </label>
            <div className="row wide">
              <SaveButton
                className="btn primary"
                busy={busy}
                busyLabel="Saving…"
                disabled={!draft.amount.trim()}
                onClick={() => void record(draft)}
              >
                Save
              </SaveButton>
              <button type="button" className="btn quiet" onClick={() => setDraft(null)}>
                Cancel
              </button>
            </div>
          </form>
        )}
        {!entries && !error && <p className="muted">Getting the spend…</p>}
        {entries && !entries.length && <p className="muted">No ad spend in the last three months.</p>}
        {entries && entries.length > 0 && (
          <ul className="campaign-list" aria-label="Spend in the last three months">
            {entries.map((e) => (
              <li key={e.id}>
                <div>
                  <b>{money(e.amountCents)}</b>
                  <span className="muted small block">{spendMeans(e)}</span>
                  {e.note && <span className="muted small block">{e.note}</span>}
                </div>
                {canEdit && editable(e) && (
                  <button
                    type="button"
                    className="btn quiet"
                    aria-label={`Remove ${money(e.amountCents)} ${spendMeans(e)}`}
                    disabled={busy}
                    onClick={() =>
                      void write(async () => {
                        await client.call('adSpend.remove', {
                          id: e.id,
                          reason: 'Removed in Settings › Campaigns',
                        });
                        return `${money(e.amountCents)} removed.`;
                      })
                    }
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <Linked
        className={bp ? 'bp-part' : 'stone card'}
        role={bp ? 'group' : undefined}
        aria-label="Linked ad campaigns"
      >
        {bp ? <h3>Linked ad campaigns</h3> : <h2 className="h3">Linked ad campaigns</h2>}
        <p className="muted">
          Which of your campaigns each Google, Microsoft or Local Services campaign is, so its spend counts
          there, past and future.
        </p>
        {links && links.unlinked.length > 0 && (
          <ul className="campaign-list" aria-label="Not on a campaign yet">
            {links.unlinked.map((u) => {
              const k = unlinkedKey(u);
              return (
                <li key={k}>
                  <div>
                    <b>{unlinkedMeans(u)}</b>
                    <span className="muted small block">
                      Not on a campaign yet: it counts as “No campaign”.
                    </span>
                  </div>
                  {canEdit && (
                    <div className="row">
                      <select
                        className="field"
                        aria-label={`Campaign for ${unlinkedMeans(u)}`}
                        value={linking[k] ?? ''}
                        onChange={(e) => setLinking({ ...linking, [k]: e.target.value })}
                      >
                        <option value="">Choose a campaign</option>
                        {live.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn soft"
                        disabled={busy || !linking[k]}
                        onClick={() =>
                          void write(async () => {
                            const r = await client.call<{ link: Link; moved: number }>(
                              'adCampaignLinks.set',
                              {
                                platform: u.platform,
                                platformCampaignId: u.platformCampaignId,
                                platformCampaignName: u.platformCampaignName,
                                campaignId: linking[k],
                              },
                            );
                            return `Linked to ${r.link.campaign.name}: ${r.moved} ${r.moved === 1 ? 'entry' : 'entries'} moved.`;
                          })
                        }
                      >
                        Link
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {links && !links.links.length && !links.unlinked.length && (
          <p className="muted">No ad platform campaigns yet.</p>
        )}
        {links && links.links.length > 0 && (
          <ul className="campaign-list" aria-label="Linked">
            {links.links.map((l) => (
              <li key={l.id}>
                <div>
                  <b>
                    {CHANNEL_WORDS[l.platform]}:{' '}
                    {l.platformCampaignName ?? l.platformCampaignId ?? 'the whole account'}
                  </b>
                  <span className="muted small block">Counts as {l.campaign.name}</span>
                </div>
                {canEdit && (
                  <button
                    type="button"
                    className="btn quiet"
                    disabled={busy}
                    aria-label={`Unlink ${l.platformCampaignName ?? l.platformCampaignId ?? 'the whole account'}`}
                    onClick={() =>
                      void write(async () => {
                        await client.call('adCampaignLinks.remove', { id: l.id });
                        return 'Unlinked: its spend is on no campaign again.';
                      })
                    }
                  >
                    Unlink
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Linked>
      {bp && <ConversionsPart canEdit={canEdit} />}
    </>
  );
}
